/* Services.
 *
 *   Today          what is on, and checking in to it
 *   This week      what is coming, saying you will be away, and how often
 *                  you have been
 *   Services       the weekly services; each opens its past service days
 *   Location       where the church is, for checking in by phone
 *
 * Everyone sees the first two, for the services they belong to. Whoever
 * manages attendance also sees the last two.
 *
 * ?checkin=<occurrence> opens the check-in sheet straight away — what a
 * reminder or the Home banner links to.
 */
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { CalendarDays, Loader2, MapPin } from 'lucide-react'
import {
  useAttendanceToday,
  useAttendanceWeek,
  useMe,
  useMyAttendance,
  useSetAway,
  useSetRegular,
} from '@/api/queries'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { useTabParam } from '@/lib/useTabParam'
import type { AttendanceToday, TodayService, WeekItem } from '@/types/api'
import { useAttendanceAccess } from './AttendancePage'
import { CheckInDialog, CheckedInCard } from './CheckIn'
import { LocationSettings } from './LocationSettings'
import { ServicesManager } from './ServicesManager'
import { StatusChip, Tabs, clock, day } from './parts'

const TABS = ['today', 'week', 'services', 'location'] as const
type Tab = (typeof TABS)[number]

export function ServicesPage() {
  const { data: me } = useMe()
  const churchId = me?.church_id ?? null
  const access = useAttendanceAccess(churchId)
  const [tab, setTab] = useTabParam<Tab>(TABS, 'today')

  const tabs: { key: Tab; label: string }[] = [
    { key: 'today', label: 'Today' },
    { key: 'week', label: 'This week' },
    ...(access.manage
      ? ([
          { key: 'services', label: 'Services' },
          { key: 'location', label: 'Location' },
        ] as { key: Tab; label: string }[])
      : []),
  ]
  const current = tabs.some((t) => t.key === tab) ? tab : 'today'

  if (!me) return null
  if (!churchId) {
    return (
      <Card className="p-s6 text-center text-base text-ink-2">
        Services belong to a church. Join one to see its services.
      </Card>
    )
  }

  return (
    <div className="space-y-s4">
      <header>
        <h1 className="text-2xl font-bold tracking-tight">Services</h1>
        <p className="mt-0.5 text-base text-ink-2">
          Check in when you arrive, and let us know when you will be away.
        </p>
      </header>

      <Tabs tabs={tabs} value={current} onChange={setTab} />

      {current === 'today' ? <TodayTab churchId={churchId} /> : null}
      {current === 'week' ? (
        <div className="space-y-s5">
          <WeekTab churchId={churchId} />
          <MineTab churchId={churchId} />
        </div>
      ) : null}
      {current === 'services' ? <ServicesManager churchId={churchId} /> : null}
      {current === 'location' ? <LocationSettings churchId={churchId} /> : null}
    </div>
  )
}

/* ------------------------------------------------------------------ today */

function TodayTab({ churchId }: { churchId: string }) {
  const today = useAttendanceToday(churchId)
  const [params, setParams] = useSearchParams()
  const [open, setOpen] = useState<string | null>(params.get('checkin'))

  function close() {
    setOpen(null)
    if (params.has('checkin')) {
      const next = new URLSearchParams(params)
      next.delete('checkin')
      setParams(next, { replace: true })
    }
  }

  if (today.isLoading) return <Spinner />
  if (today.isError || !today.data) {
    return (
      <Card className="p-s6 text-center text-base text-ink-2">
        {(today.error as Error)?.message || 'Today’s services did not load.'}
      </Card>
    )
  }

  const t = today.data

  return (
    <div className="space-y-s3">
      {!t.services.length ? (
        <Card className="p-s8 text-center">
          <CalendarDays className="mx-auto h-8 w-8 text-ink-2" aria-hidden />
          <p className="mt-s3 text-md font-bold">Nothing on today</p>
          <p className="mt-1 text-base text-ink-2">
            See This week for what is coming up.
          </p>
        </Card>
      ) : null}

      {t.services.map((s) => (
        <ServiceToday
          key={s.occurrence_id}
          churchId={churchId}
          service={s}
          today={t}
          onCheckIn={() => setOpen(s.occurrence_id)}
        />
      ))}

      {open && t.services.some((s) => s.occurrence_id === open) ? (
        <CheckInDialog churchId={churchId} occurrenceId={open} today={t} onClose={close} />
      ) : null}
    </div>
  )
}

function ServiceToday({
  churchId,
  service: s,
  today,
  onCheckIn,
}: {
  churchId: string
  service: TodayService
  today: AttendanceToday
  onCheckIn: () => void
}) {
  return (
    <Card className="space-y-s3 p-s4">
      <div>
        <h2 className="text-lg font-bold">{s.name}</h2>
        <p className="text-sm text-ink-2">{day(s.date, true)}</p>
      </div>

      <ul className="divide-y divide-line-2 rounded border border-line-2">
        {s.slots.map((slot) => (
          <li
            key={slot.id}
            className={cn(
              'flex items-center gap-s3 px-s3 py-s2',
              slot.id === s.current_slot_id && 'bg-brand-softer',
            )}
          >
            <span className="min-w-0 flex-1">
              <span className="block truncate text-base font-semibold">{slot.label}</span>
              <span className="block text-sm text-ink-2">
                {clock(slot.starts_at)} – {clock(slot.ends_at)}
              </span>
            </span>
            <span
              className={cn(
                'shrink-0 rounded-pill px-s2 py-0.5 text-xs font-semibold',
                slot.state === 'open'
                  ? 'bg-success-soft text-success-ink'
                  : 'bg-surface-2 text-ink-2',
              )}
            >
              {slot.state === 'open' ? 'On now' : slot.state === 'over' ? 'Over' : 'Later'}
            </span>
          </li>
        ))}
      </ul>

      {s.checked_in ? (
        <CheckedInCard churchId={churchId} occurrenceId={s.occurrence_id} checkedIn={s.checked_in} />
      ) : s.is_on && s.current_slot_id ? (
        <Button variant="primary" size="lg" full onClick={onCheckIn}>
          <MapPin className="h-5 w-5" aria-hidden />
          Check in{s.current_slot_label ? ` · ${s.current_slot_label}` : ''}
        </Button>
      ) : (
        <p className="text-sm text-ink-2">
          {s.slots.every((x) => x.state === 'over')
            ? 'This service is over for today.'
            : 'Check-in opens 30 minutes before each time.'}
        </p>
      )}

      {!today.location_set && !s.checked_in ? (
        <p className="text-sm text-ink-2">
          Checking in by phone is not set up at {today.church_name} yet. Ask
          someone at the welcome desk to mark you present.
        </p>
      ) : null}
    </Card>
  )
}

/* ------------------------------------------------------------------- week */

function WeekTab({ churchId }: { churchId: string }) {
  const week = useAttendanceWeek(churchId)
  if (week.isLoading) return <Spinner />
  if (week.isError) {
    return (
      <Card className="p-s6 text-center text-base text-ink-2">
        {(week.error as Error).message}
      </Card>
    )
  }
  const rows = week.data ?? []
  if (!rows.length) {
    return (
      <Card className="p-s8 text-center text-base text-ink-2">
        No services in the next seven days.
      </Card>
    )
  }
  return (
    <Card className="divide-y divide-line-2">
      {rows.map((w) => (
        <WeekRow key={`${w.service_id}-${w.date}`} churchId={churchId} item={w} />
      ))}
    </Card>
  )
}

function WeekRow({ churchId, item: w }: { churchId: string; item: WeekItem }) {
  const away = useSetAway(churchId)
  const regular = useSetRegular(churchId)
  // A service members choose: nobody is expected until they say they attend
  // it, so "away" only means something once they do.
  const chosen = Boolean(w.members_choose)
  const expected = !chosen || Boolean(w.regular)
  const failed = away.isError ? away.error : regular.isError ? regular.error : null
  return (
    <div className="flex flex-wrap items-center gap-s3 px-s4 py-s3">
      <div className="min-w-0 flex-1">
        <p className="text-base font-semibold">{w.name}</p>
        <p className="text-sm text-ink-2">
          {day(w.date, true)}
          {w.slots.length
            ? ` · ${w.slots.map((s) => `${clock(s.starts_at)} ${s.label}`).join(' · ')}`
            : ''}
        </p>
        {chosen ? (
          <p className="mt-0.5 text-sm text-ink-3">
            Only people who attend regularly are expected. Anyone is welcome.
          </p>
        ) : null}
        {failed ? (
          <p role="alert" className="mt-1 text-sm text-danger">
            {(failed as Error).message}
          </p>
        ) : null}
      </div>
      <div className="flex shrink-0 flex-col items-end gap-s2">
        {chosen ? (
          <Toggle
            label="Attend regularly"
            on={Boolean(w.regular)}
            busy={regular.isPending}
            onChange={(on) => regular.mutate({ serviceId: w.service_id, regular: on })}
          />
        ) : null}
        {expected ? (
          <Toggle
            label="I’ll be away"
            on={w.away}
            busy={away.isPending}
            onChange={(on) => away.mutate({ date: w.date, away: on })}
          />
        ) : null}
      </div>
    </div>
  )
}

function Toggle({
  label,
  on,
  busy,
  onChange,
}: {
  label: string
  on: boolean
  busy: boolean
  onChange: (on: boolean) => void
}) {
  return (
    <label className="flex cursor-pointer items-center gap-s2 text-sm font-semibold text-ink-2">
      <span>{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        disabled={busy}
        onClick={() => onChange(!on)}
        className={cn(
          'relative h-6 w-11 rounded-pill transition-colors disabled:opacity-60',
          on ? 'bg-brand' : 'bg-hover-2',
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 h-5 w-5 rounded-pill bg-white shadow-1 transition-all',
            on ? 'left-[22px]' : 'left-0.5',
          )}
        />
      </button>
    </label>
  )
}

/* ------------------------------------------------------------------- mine */

function MineTab({ churchId }: { churchId: string }) {
  const mine = useMyAttendance(churchId)
  if (mine.isLoading) return <Spinner />
  if (mine.isError || !mine.data) {
    return (
      <Card className="p-s6 text-center text-base text-ink-2">
        {(mine.error as Error)?.message}
      </Card>
    )
  }
  const { items, summary } = mine.data
  return (
    <section className="space-y-s3">
      <h2 className="text-md font-bold">My attendance</h2>
      {!items.length ? (
        <Card className="p-s6 text-center text-base text-ink-2">
          Once you have been to a service, it shows here.
        </Card>
      ) : null}
      {summary.length ? (
        <Card className="space-y-1 p-s4">
          {summary.map((s) => (
            <p key={s.service_id} className="text-base">
              Present <strong className="font-extrabold">{s.present}</strong> of the
              last {s.of} {s.name}
            </p>
          ))}
        </Card>
      ) : null}
      {items.length ? (
        <Card className="divide-y divide-line-2">
          {items.map((i) => (
            <div key={i.occurrence_id} className="flex items-center gap-s3 px-s4 py-s3">
              <div className="min-w-0 flex-1">
                <p className="truncate text-base font-semibold">{i.name}</p>
                <p className="text-sm text-ink-2">
                  {day(i.date)}
                  {i.slot_label ? ` · ${i.slot_label}` : ''}
                  {i.checked_in_at ? ` · ${clock(i.checked_in_at)}` : ''}
                </p>
              </div>
              <StatusChip status={i.status} />
            </div>
          ))}
        </Card>
      ) : null}
    </section>
  )
}

function Spinner() {
  return (
    <Card className="flex justify-center py-s6">
      <Loader2 className="h-5 w-5 animate-spin text-ink-2" aria-label="Loading" />
    </Card>
  )
}
